/**
 * Justified-row layout for the issue panel's attachment gallery: every tile
 * keeps its own aspect ratio, and each full row is scaled to span the gallery
 * exactly — the photo-grid pattern, rather than one image per line.
 *
 * Pure arithmetic over CSS pixels so it can be unit-tested headlessly. The
 * inputs that make it responsive are the measured gallery width (the panel is
 * resizable), the viewport height (which browser zoom changes, in CSS pixels)
 * and the number of tiles.
 */

export interface GalleryTileBox {
  /** Index into the aspect-ratio list the layout was computed from. */
  index: number;
  width: number;
}

export interface GalleryRow {
  height: number;
  tiles: GalleryTileBox[];
}

/** Fraction of the viewport one row aims for, by how many tiles there are:
 *  a lone attachment can be large, a dozen should read as a contact sheet. */
function rowHeightFactor(count: number): number {
  if (count <= 1) return 0.42;
  if (count === 2) return 0.32;
  if (count <= 4) return 0.24;
  return 0.18;
}

/** Never smaller than this, so a zoomed-in window still shows a legible tile. */
export const MIN_ROW_HEIGHT_PX = 72;
/** Never taller than this, so a tall monitor does not get poster-sized rows. */
export const MAX_ROW_HEIGHT_PX = 480;

/** The height a row aims for before it is stretched to fill the width. */
export function targetRowHeight(viewportHeight: number, count: number): number {
  const ideal = viewportHeight * rowHeightFactor(count);
  return Math.min(MAX_ROW_HEIGHT_PX, Math.max(MIN_ROW_HEIGHT_PX, ideal));
}

/** How much of the viewport the collapsed gallery may take before the rest
 *  of the issue (comments, checklist) is pushed out of sight. */
const COLLAPSED_VIEWPORT_FRACTION = 0.55;

/** Height the collapsed gallery may fill; always room for at least one row. */
export function collapsedHeightBudget(viewportHeight: number, rowHeight: number): number {
  return Math.max(viewportHeight * COLLAPSED_VIEWPORT_FRACTION, rowHeight);
}

/** `log` distance, so a row twice the target and one half of it are equally far. */
function distanceFromTarget(height: number, target: number): number {
  return Math.abs(Math.log(height / target));
}

function closeRow(indices: number[], aspectRatios: number[], height: number): GalleryRow {
  return {
    height,
    tiles: indices.map((index) => ({ index, width: aspectRatios[index] * height })),
  };
}

/**
 * Packs tiles into rows. A row closes once it reaches the gallery width, and
 * the tile that tipped it over goes in whichever row lands closer to the target
 * height — so a wide panorama starts a new row instead of squashing three
 * photos into a strip. Full rows are scaled to span `containerWidth` exactly;
 * the last, unfilled row keeps the target height.
 */
export function layoutJustifiedRows(
  aspectRatios: number[],
  {
    containerWidth,
    targetHeight,
    gap,
  }: { containerWidth: number; targetHeight: number; gap: number }
): GalleryRow[] {
  if (containerWidth <= 0 || aspectRatios.length === 0) return [];

  const rows: GalleryRow[] = [];
  let current: number[] = [];
  let aspectSum = 0;

  /** The height at which `count` tiles summing to `sum` fill the width. */
  const fillHeight = (sum: number, count: number) => (containerWidth - gap * (count - 1)) / sum;

  aspectRatios.forEach((aspect, index) => {
    const withSum = aspectSum + aspect;
    const withCount = current.length + 1;
    if (withSum * targetHeight + gap * (withCount - 1) < containerWidth) {
      current.push(index);
      aspectSum = withSum;
      return;
    }

    const withHeight = fillHeight(withSum, withCount);
    if (current.length > 0) {
      // Taller than the target (the row did not reach the width at it), where
      // `withHeight` is shorter; whichever is nearer wins.
      const withoutHeight = fillHeight(aspectSum, current.length);
      if (
        distanceFromTarget(withoutHeight, targetHeight) <
        distanceFromTarget(withHeight, targetHeight)
      ) {
        rows.push(closeRow(current, aspectRatios, withoutHeight));
        current = [index];
        aspectSum = aspect;
        // A lone tile already wider than the gallery fills the row by itself.
        if (aspect * targetHeight >= containerWidth) {
          rows.push(closeRow(current, aspectRatios, fillHeight(aspect, 1)));
          current = [];
          aspectSum = 0;
        }
        return;
      }
    }

    current.push(index);
    rows.push(closeRow(current, aspectRatios, withHeight));
    current = [];
    aspectSum = 0;
  });

  if (current.length > 0) rows.push(closeRow(current, aspectRatios, targetHeight));
  return rows;
}

/**
 * How many leading rows the collapsed gallery shows: as many as fit in
 * `budget` (gaps included), and never fewer than one.
 */
export function rowsWithinBudget(rows: GalleryRow[], budget: number, gap: number): number {
  let used = 0;
  let count = 0;
  for (const row of rows) {
    const next = used + (count > 0 ? gap : 0) + row.height;
    if (count > 0 && next > budget) break;
    used = next;
    count += 1;
  }
  return count;
}
